import { ApiError, csrfHeaders, forgetCsrfToken, toApiError } from '@/lib/http';
import { parseKhutbahReading, type KhutbahReading } from '@/features/reader/types';
import {
  parseAlignmentTargets,
  parseEntryResult,
  parsePublishResult,
  type AlignmentTarget,
  type EntryResult,
  type Provenance,
  type PublishResult,
} from './editorTypes';

/**
 * The editor's requests.
 *
 * Every read takes the `AbortSignal` through to `fetch`, so switching khutbah
 * cancels the outgoing request rather than merely ignoring its answer.
 *
 * The writes deliberately do **not**: entering a translation and publishing
 * are not idempotent from the client's side of the wire, and aborting one
 * mid-flight tells us nothing about whether the server completed it. They are
 * short, and letting them finish is the honest behaviour.
 */

/** The passages that need a translation, in order, Qur'an excluded. */
export async function fetchAlignment(
  khutbahId: string,
  signal: AbortSignal,
): Promise<AlignmentTarget[]> {
  const response = await fetch(
    `/api/editor/khutbah/${encodeURIComponent(khutbahId)}/alignment`,
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parseAlignmentTargets(await response.json());
}

/**
 * The khutbah as the editor sees it — drafts included.
 *
 * Needed alongside the alignment because the alignment omits Qur'an, and the
 * editor has to *see* those passages to know where they sit. They are shown,
 * never offered as fields.
 */
export async function fetchEditorReading(
  khutbahId: string,
  language: string,
  signal: AbortSignal,
): Promise<KhutbahReading> {
  const response = await fetch(
    `/api/editor/khutbah/${encodeURIComponent(khutbahId)}?language=${encodeURIComponent(language)}`,
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parseKhutbahReading(await response.json());
}

async function write(
  url: string,
  body: unknown,
  signal: AbortSignal,
): Promise<unknown> {
  const csrf = await csrfHeaders(signal);
  const response = await fetch(url, {
    method: 'POST',
    headers: { ...csrf, Accept: 'application/json', 'Content-Type': 'application/json' },
    // Spread rather than `body: undefined`: under exactOptionalPropertyTypes
    // an absent property and one set to undefined are different types, and a
    // bodyless POST is what the publish endpoint wants.
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    // A stale token survives a server restart; clearing it means the next
    // attempt fetches a fresh one rather than failing the same way forever.
    if (response.status === 401 || response.status === 403) {
      forgetCsrfToken();
    }
    throw await toApiError(response);
  }
  return response.json();
}

/**
 * Stores the whole translation, one paragraph per translatable passage.
 *
 * All of it or none: the server wraps the insert in one transaction, because
 * a half-entered translation would publish passage by passage and read as a
 * truncated khutbah.
 *
 * @param declaredModel required when the origin is `ai` — a reader is told
 *                      which model produced a translation, so it cannot be
 *                      left for someone to fill in later.
 */
export async function enterTranslation(
  khutbahId: string,
  language: string,
  origin: Provenance,
  declaredModel: string | null,
  paragraphs: readonly string[],
  signal: AbortSignal,
): Promise<EntryResult> {
  return parseEntryResult(
    await write(
      `/api/editor/khutbah/${encodeURIComponent(khutbahId)}/translations`,
      { language, origin, declaredModel, paragraphs },
      signal,
    ),
  );
}

/** Approves this language and makes the khutbah readable. */
export async function publishTranslation(
  khutbahId: string,
  language: string,
  signal: AbortSignal,
): Promise<PublishResult> {
  return parsePublishResult(
    await write(
      `/api/editor/khutbah/${encodeURIComponent(khutbahId)}/publish?language=${encodeURIComponent(language)}`,
      undefined,
      signal,
    ),
  );
}

export { ApiError };
